import { useState } from 'react';

/**
 * Single-series column chart with a hover tooltip and an accessible table.
 * Marks: ≤24px columns, 4px rounded tops, square at the baseline, hairline solid grid.
 */
export function ColumnChart({
  data,
  format,
  height = 180,
  label,
  detail,
}: {
  data: { key: string; label: string; value: number }[];
  format: (v: number) => string;
  height?: number;
  label: string;
  detail?: (i: number) => React.ReactNode;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(...data.map((d) => d.value), 1);
  const nice = niceMax(max);
  const ticks = [0, nice / 2, nice];
  const W = 640;
  const padL = 44;
  const padB = 22;
  const plotW = W - padL - 4;
  const plotH = height - padB - 8;
  const band = plotW / Math.max(data.length, 1);
  const barW = Math.min(24, band - 4);
  const y = (v: number) => 8 + plotH - (Math.max(v, 0) / nice) * plotH;
  return (
    <figure className="relative m-0">
      <svg viewBox={`0 0 ${W} ${height}`} className="h-auto w-full" role="img" aria-label={label} onMouseLeave={() => setHover(null)}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} x2={W - 4} y1={y(t)} y2={y(t)} stroke="var(--color-line)" strokeWidth={1} />
            <text x={padL - 6} y={y(t) + 4} textAnchor="end" className="fill-[var(--color-muted)] text-[11px]">
              {compact(t, format)}
            </text>
          </g>
        ))}
        {data.map((d, i) => {
          const x = padL + i * band + (band - barW) / 2;
          const top = y(d.value);
          const h = 8 + plotH - top;
          const r = Math.min(4, h);
          return (
            <g key={d.key} onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)} tabIndex={0} aria-label={`${d.label}: ${format(d.value)}`}>
              <rect x={padL + i * band} y={8} width={band} height={plotH} fill="transparent" />
              {h > 0 && (
                <path
                  d={`M${x},${8 + plotH} L${x},${top + r} Q${x},${top} ${x + r},${top} L${x + barW - r},${top} Q${x + barW},${top} ${x + barW},${top + r} L${x + barW},${8 + plotH} Z`}
                  fill="var(--color-chart)"
                  opacity={hover == null || hover === i ? 1 : 0.45}
                />
              )}
              {(() => {
                const step = Math.ceil(data.length / 7);
                const last = data.length - 1;
                return i === last || (i % step === 0 && last - i >= step);
              })() && (
                <text x={padL + i * band + band / 2} y={height - 6} textAnchor="middle" className="fill-[var(--color-muted)] text-[11px]">
                  {d.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {hover != null && data[hover] && (
        <div
          className="pointer-events-none absolute top-0 z-10 -translate-x-1/2 rounded-[8px] border border-line bg-surface px-3 py-2 text-sm shadow-lg"
          style={{ left: `${((padL + hover * band + band / 2) / W) * 100}%` }}
        >
          <p className="font-medium">{data[hover].label}</p>
          <p className="num">{format(data[hover].value)}</p>
          {detail?.(hover)}
        </div>
      )}
      <table className="sr-only">
        <caption>{label}</caption>
        <tbody>
          {data.map((d) => (
            <tr key={d.key}>
              <th>{d.label}</th>
              <td>{format(d.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

/** Horizontal bars for a ranked list (payment mix, top categories). Value at the tip, text in ink. */
export function BarList({ items, format }: { items: { label: string; value: number; note?: string }[]; format: (v: number) => string }) {
  const max = Math.max(...items.map((i) => i.value), 1);
  return (
    <ul className="space-y-2.5">
      {items.map((i) => (
        <li key={i.label}>
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="truncate">{i.label}</span>
            <span className="num font-medium">{format(i.value)}</span>
          </div>
          <div className="mt-1 h-2 rounded-full bg-paper">
            <div className="h-2 rounded-full bg-[var(--color-chart)]" style={{ width: `${Math.max((i.value / max) * 100, i.value > 0 ? 2 : 0)}%` }} />
          </div>
          {i.note && <p className="mt-0.5 text-xs text-muted">{i.note}</p>}
        </li>
      ))}
    </ul>
  );
}

function niceMax(v: number) {
  const p = 10 ** Math.floor(Math.log10(v));
  const m = v / p;
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * p;
}

function compact(cents: number, format: (v: number) => string) {
  const dollars = cents / 100;
  if (dollars >= 1000) return `${Math.round(dollars / 100) / 10}K`;
  if (cents === 0) return '0';
  return format(cents).replace(/^[^\d-]+/, '').replace(/\.00$/, '');
}
